import React, { useState } from 'react';
import {
  Container, Typography, TextField, Button, Box, RadioGroup,
  FormControlLabel, Radio, Checkbox, FormGroup, FormLabel, Paper, Slide, IconButton
} from '@mui/material';
import ChatSharpIcon from '@mui/icons-material/ChatSharp';
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function CustomSnackbar({ open, message, severity, handleClose }) {
  const autoHideDuration = 6000;

  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorOutlineIcon sx={{ mr: 1 }} />;
      case 'success':
      default:
        return <CheckCircleOutlineIcon sx={{ mr: 1 }} />;
    }
  };

  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, handleClose]);

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1400,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            backgroundColor: severity === 'error' ? '#f44336' : '#4caf50',
            padding: '10px 24px',
            borderRadius: 4,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            minWidth: '300px',
            maxWidth: '80vw',
          }}
        >
          {getIcon()}
          <Typography sx={{ flexGrow: 1 }}>{message}</Typography>
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Paper>
      </Box>
    </Slide>
  );
}

function App() {
  const [reasons, setReasons] = useState([]);
  const [comments, setComments] = useState('');
  const [canContact, setCanContact] = useState('Yes');
  const [openToAdjusting, setOpenToAdjusting] = useState('No');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [submitted, setSubmitted] = useState(false);

  const emailLogId = new URLSearchParams(window.location.search).get('emailLogId');
  const responseType = new URLSearchParams(window.location.search).get('responseType');

  const resetForm = () => {
    setReasons([]);
    setComments('');
    setCanContact('Yes');
    setOpenToAdjusting('No');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleInterestedNow = async () => {
    const payload = {
      emailLogId,
      responseType: 'Interested',
      comments: 'Seller initially declined but changed mind via feedback form.',
      reasons: [],
      canContact: 'Yes'
    };

    try {
      const res = await fetch(process.env.REACT_APP_N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        showSnackbar("Great! We've noted your interest. We'll notify the buyer.");
        resetForm();
        setTimeout(() => setSubmitted(true), 5000);
      } else {
        showSnackbar('Failed to update your response.', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnackbar('Something went wrong.', 'error');
    }
  };

  const handleNotInterestSubmit = async () => {
    const payload = {
      emailLogId,
      responseType: responseType,
      reasons,
      comments,
      canContact,
      openToAdjusting,
    };

    try {
      const res = await fetch(process.env.REACT_APP_N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        showSnackbar('Thank you! Your feedback has been submitted.');
        resetForm();
        setTimeout(() => setSubmitted(true), 5000);
      } else {
        showSnackbar('Failed to submit feedback.', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnackbar('Something went wrong. Please try again.', 'error');
    }
  };

  const handleReasonChange = (event) => {
    const value = event.target.value;
    setReasons((prev) =>
      prev.includes(value)
        ? prev.filter((r) => r !== value)
        : [...prev, value]
    );
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          🎉 Thank you!
        </Typography>
        <Typography variant="body1">
          Your response has been successfully recorded.
        </Typography>
      </Container>
    );
  }

  const renderReasons = () => {
    if (responseType === 'Interested') return null;

    const label = responseType === 'Need More Info'
      ? '💡 What additional information are you looking for?'
      : '📅 Why is this trade opportunity not of Interest?';

    const options = responseType === 'Need More Info'
      ? ['Price', 'Quantity', 'Shipping', 'Payment', 'Other']
      : ['Price too low', 'Insufficient quantity', 'Shipping terms', 'Payment terms', 'Other'];

    return (
      <Box mt={3}>
        <FormLabel>{label}</FormLabel>
        <FormGroup>
          {options.map((reason) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={reasons.includes(reason)}
                  onChange={handleReasonChange}
                  value={reason}
                />
              }
              label={reason}
              key={reason}
            />
          ))}
        </FormGroup>

        {responseType === 'Not Interested' && (
          <Box mt={3}>
            <FormLabel>🔄 Is the seller open to adjusting the deal?</FormLabel>
            <RadioGroup
              row
              value={openToAdjusting}
              onChange={(e) => setOpenToAdjusting(e.target.value)}
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{
        backgroundColor: '#e3f2fd',
        borderLeft: '5px solid #2196F3',
        padding: 2,
        borderRadius: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <ChatSharpIcon sx={{ color: '#2196F3' }} />
        Feedback to the Buyer
      </Typography>

      {renderReasons()}

      <Box mt={3}>
        <TextField
          fullWidth
          label="💬 Additional Comments (optional)"
          multiline
          rows={4}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
      </Box>

      <Box mt={3}>
        <FormLabel>📞 Can we contact you about this feedback?</FormLabel>
        <RadioGroup
          row
          value={canContact}
          onChange={(e) => setCanContact(e.target.value)}
        >
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
      </Box>

      <Typography variant="body2" sx={{ mt: 3 }}>
        Your feedback helps us enhance TradeVow’s intelligent matchmaking engine, products, and services. Thank You.
      </Typography>

      <Box mt={4} display="flex" gap={2}>
        {(responseType === 'Not Interested') && (
          <>
            <Button
              variant="contained"
              size="medium"
              color="success"
              startIcon="✅"
              onClick={handleInterestedNow}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="flex-start" lineHeight={2}>
                <span>Actually, I am Interested</span>
              </Box>
            </Button>

            <Button
              variant="contained"
              size="medium"
              color="error"
              startIcon="❌"
              onClick={handleNotInterestSubmit}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              OK, to miss this opportunity
            </Button>
          </>
        )}

        {(responseType === 'Need More Info') && (
          <Button
            variant="contained"
            size="medium"
            startIcon="📩"
            onClick={handleNotInterestSubmit}
            sx={{
              backgroundColor: '#ffa726',
              color: '#fff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#fb8c00',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              },
              '&:active': {
                transform: 'scale(0.97)',
              },
            }}
          >
            Submit Request for More Info
          </Button>
        )}

        {(responseType === 'Interested') && (
          <Button
            variant="contained"
            size="medium"
            color="success"
            startIcon="✅"
            onClick={handleInterestedNow}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="flex-start" lineHeight={2}>
              <span>Actually, I am Interested</span>
            </Box>
          </Button>
        )}
      </Box>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default App;
